// internal deps
import React, { useCallback, useEffect, useMemo, useState, useContext, useRef } from "react";

// helpers
import { getClosingTimeEpochFromStorage, isEmpty, isValidNumber, scrollElementIntoView } from "@swiggy-private/core-ui";
import { isEqual } from "@swiggy-private/js-utils";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import {
    isRestaurantSameAsPrevious,
    storeRestaurantId,
    isEcoSaverCroutonActive,
    storeOverlayVisibility,
    getOverlayVisibility,
    getEcoSaverToastData,
    getRestaurantId,
} from "@cart-client/helpers/Cart";
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { checkServiceability } from "@cart-client/helpers/CartDeliverySpeed";

// constants
import { CART_ELEMENT_IDS } from "@cart-client/components/cart-renderer/constants";
import { FREQUENCY_CAP_PAYLOAD } from "./constant";

// styles
import styles from "./EcosaverCrouton.scss";
import { ToastActionButtonWrapper, ToastActionButton } from "@cart-client/components/delivery-section-renderer/styles";

//sessionStorage
import {
    CartOverlaysContext,
    DELIVERY_SECTION_ELEMENT,
    ProceedMode,
    useLocalStorageBasedFrequencyCapping,
} from "@swiggy-private/checkout-core";

// types & data-testid's
import {
    CroutonIds,
    CroutonPriority,
    DELIVERY_SPEED,
    DISCOUNT_TYPE,
    GenericCroutonProps,
} from "@swiggy-private/cart-types";
import { IUseEcoSaverCrouton } from "./types";
import { CROUTON } from "@swiggy-private/cart-test-ids";

// context
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";
import { getClosingTimeFromApiInSeconds } from "@cart-client/helpers/TimerOnCart";

export interface IUseEcoSaverCroutonProps {
    setCroutonVisibility: (visibility: boolean) => void;
}

const useEcoSaverCrouton = ({ setCroutonVisibility }: IUseEcoSaverCroutonProps): IUseEcoSaverCrouton => {
    const [showExpressLottie, setShowExpressLottie] = useState<boolean>(false);
    const [ecoSaverToastVisibility, setEcoSaverToastVisibility] = useState(() => {
        const { ecoSaverLottie = true } = getOverlayVisibility();
        return ecoSaverLottie;
    });
    const initialApiDeliveryTypeRef = useRef<string | undefined>(undefined);

    const { registerCrouton, enabledCroutonId, onCloseCrouton } = useContext(CartOverlaysContext);
    const { setDeliveryTab } = useContext(DeliverySectionContext);
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);
    const { onImpressionEcoSaverToast, onClickEcoSaverToast, onImpressionEcoSaverLottie } =
        useCartRendererAnalytics(cartResponse);

    const isCartStatusSuccess = useMemo(
        () => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS,
        [cartResponse?.statusCode],
    );

    const { selectedAddress } = useFetchAddressOnClick(
        cartResponse?.data?.cart_total,
        cartResponse?.data?.addresses,
        isCartStatusSuccess,
    );

    const userLoggedIn = isUserLoggedIn();

    // to check whether rx is unservicable
    const isRxUnservicable = useMemo(
        () =>
            checkServiceability(
                cartResponse?.statusCode,
                { userLoggedIn, selectedAddress },
                CART_STATUS_CODES.RESTAURANT_UNSERVICEABLE,
                0,
            ),
        [cartResponse?.statusCode, selectedAddress, userLoggedIn],
    );

    // to check whether location is unservicable
    const isLocationServicable = useMemo(
        () =>
            checkServiceability(
                cartResponse?.statusCode,
                { userLoggedIn, selectedAddress },
                CART_STATUS_CODES.SUCCESS,
                1,
            ),
        [cartResponse?.statusCode, selectedAddress, userLoggedIn],
    );

    const handleShowEcoSaverCrouton = useCallback(() => {
        setShowExpressLottie(true);
    }, []);

    const { ecoSaverCroutonSuccessInfo, ecoSaverLottieSuccess, restaurantId, isRestaurantSame } = useMemo(() => {
        if ((!isRxUnservicable && !isLocationServicable) || isEmpty(cartResponse)) {
            return {
                ecoSaverCroutonSuccessInfo: null,
                ecoSaverLottieSuccess: null,
                restaurantId: null,
                isRestaurantSame: true,
                isEcoSaverSelected: null,
            };
        }

        const ecoSaverCroutonSuccessInfo = getEcoSaverToastData(cartResponse);

        const restaurantId = getRestaurantId(cartResponse);

        const isRestaurantSame = isRestaurantSameAsPrevious(restaurantId);
        const isEcoSaverSelected = selectedAddress?.selected_sla_option === DELIVERY_SPEED.BATCH_NO_RUSH;

        if (initialApiDeliveryTypeRef.current === null)
            initialApiDeliveryTypeRef.current = selectedAddress?.selected_sla_option;

        const ecoSaverLottieSuccess = {
            showExpressLottie,
            setShowExpressLottie,
            lottieId: ecoSaverCroutonSuccessInfo?.lottie_id,
            onImpressionEcoSaverLottie: onImpressionEcoSaverLottie,
            cartId: cartResponse?.data?.cartId,
            addressId: cartResponse?.data?.address_id,
            discountType: ecoSaverCroutonSuccessInfo?.discountType as DISCOUNT_TYPE,
        };

        return {
            ecoSaverCroutonSuccessInfo,
            restaurantId,
            isRestaurantSame,
            ecoSaverLottieSuccess,
            isEcoSaverSelected,
        };
    }, [
        cartResponse,
        isLocationServicable,
        isRxUnservicable,
        onImpressionEcoSaverLottie,
        selectedAddress?.selected_sla_option,
        showExpressLottie,
    ]);

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping(FREQUENCY_CAP_PAYLOAD);

    useEffect(() => {
        if (!isEmpty(cartResponse) && !isRestaurantSame) {
            storeOverlayVisibility({ ecoSaverLottie: false });
            setEcoSaverToastVisibility(false);
        }
    }, [cartResponse, isRestaurantSame]);

    useEffect(() => {
        if (isEcoSaverCroutonActive(enabledCroutonId)) {
            storeRestaurantId(restaurantId);
        }
    }, [enabledCroutonId, restaurantId]);

    const handleCloseCrouton = useCallback(() => {
        onCloseCrouton();
        setCroutonVisibility(false);

        storeOverlayVisibility({ ecoSaverLottie: true });
        setEcoSaverToastVisibility(true);

        shouldProceed();
    }, [onCloseCrouton, setCroutonVisibility, shouldProceed]);

    const handleScrollToDeliverySection = useCallback(() => {
        scrollElementIntoView(CART_ELEMENT_IDS.DE_TIP_CONTAINER, true);
        setDeliveryTab(DELIVERY_SECTION_ELEMENT.DELIVERY_TYPE);
        handleCloseCrouton();
        setShowExpressLottie(true);

        onClickEcoSaverToast(
            cartResponse?.data?.cartId,
            ecoSaverCroutonSuccessInfo?.message,
            cartResponse?.data?.address_id,
        );
    }, [cartResponse, ecoSaverCroutonSuccessInfo?.message, handleCloseCrouton, onClickEcoSaverToast, setDeliveryTab]);

    const croutonContent: GenericCroutonProps = useMemo(
        () => ({
            title: ecoSaverCroutonSuccessInfo?.message,
            postTextEl: (
                <ToastActionButtonWrapper>
                    <ToastActionButton
                        data-testid={CROUTON.ECO_SAVER_TOAST}
                        onClick={handleScrollToDeliverySection}
                        buttonType="primary"
                        buttonSize="small"
                    >
                        {ecoSaverCroutonSuccessInfo?.ctaText}
                    </ToastActionButton>
                </ToastActionButtonWrapper>
            ),
            buttonClassName: styles.ecoSaverCroutonButton,
            icon: ecoSaverCroutonSuccessInfo?.icon,
            iconClassName: styles.ecoSaverCroutonIcon,
        }),
        [ecoSaverCroutonSuccessInfo, handleScrollToDeliverySection],
    );

    const allowEcoCroutonOnRestaurantTimerCheck = useMemo(() => {
        // NOTE - checking both closing duration and epoch in case local storage is not updated
        // pull out restaurant closing time from storage
        const restaurantCloseEpoch = getClosingTimeEpochFromStorage();
        // pull out restaurant timer data for comparison
        const restaurantClosingTimeInSec = getClosingTimeFromApiInSeconds(cartResponse);

        // CASE 1 - Restaurant close epoch is present and is valid in storage
        if (isValidNumber(restaurantCloseEpoch) && restaurantCloseEpoch > Date.now()) {
            return false;
        }
        // CASE 2 - Restaurant closing time is present in response
        else if (isValidNumber(restaurantClosingTimeInSec) && restaurantClosingTimeInSec > 0) {
            return false;
        }
        // CASE 3 - Restaurant time is not present in storage or is invalid so enable eco saver crouton
        return true;
    }, [cartResponse]);

    useEffect(() => {
        const shouldShowEcoSaverCrouton = !isEmpty(ecoSaverCroutonSuccessInfo);
        if (
            shouldShowEcoSaverCrouton &&
            allowEcoCroutonOnRestaurantTimerCheck &&
            (shouldProceed({ mode: ProceedMode.ONLY_CHECK }) || !ecoSaverToastVisibility)
        ) {
            registerCrouton({
                id: CroutonIds.FREE_DEL_AND_CASHBACK_TOAST,
                priority: CroutonPriority.PRIORITY_ORDER_ONE,
                content: croutonContent,
            });
            setCroutonVisibility(true);
            onImpressionEcoSaverToast(
                cartResponse?.data?.cartId,
                ecoSaverCroutonSuccessInfo?.message,
                cartResponse?.data?.address_id,
            );
        } else if (!allowEcoCroutonOnRestaurantTimerCheck || !shouldShowEcoSaverCrouton) {
            if (isEcoSaverCroutonActive(enabledCroutonId) && !isEmpty(cartResponse)) {
                onCloseCrouton();
                setCroutonVisibility(false);
                shouldProceed();
            }
        }
    }, [
        allowEcoCroutonOnRestaurantTimerCheck,
        cartResponse,
        croutonContent,
        ecoSaverCroutonSuccessInfo,
        ecoSaverToastVisibility,
        enabledCroutonId,
        handleCloseCrouton,
        onCloseCrouton,
        onImpressionEcoSaverToast,
        registerCrouton,
        setCroutonVisibility,
        shouldProceed,
    ]);

    // This section check if initially eco saver selected or not
    useEffect(() => {
        if (
            !isEmpty(cartResponse) &&
            !ecoSaverToastVisibility &&
            initialApiDeliveryTypeRef.current === selectedAddress?.selected_sla_option
        ) {
            storeOverlayVisibility({ ecoSaverLottie: false });
            setEcoSaverToastVisibility(false);
        } else if (
            !isEmpty(cartResponse) &&
            isEcoSaverCroutonActive(enabledCroutonId) &&
            initialApiDeliveryTypeRef.current !== undefined &&
            initialApiDeliveryTypeRef.current !== DELIVERY_SPEED.BATCH_NO_RUSH &&
            isEqual(selectedAddress?.selected_sla_option, DELIVERY_SPEED.BATCH_NO_RUSH)
        ) {
            handleCloseCrouton();
        }
        initialApiDeliveryTypeRef.current = selectedAddress?.selected_sla_option;
    }, [
        cartResponse,
        enabledCroutonId,
        selectedAddress?.selected_sla_option,
        handleCloseCrouton,
        ecoSaverToastVisibility,
    ]);

    useEffect(() => {
        if (
            isEcoSaverCroutonActive(enabledCroutonId) &&
            showExpressLottie &&
            (shouldProceed() || !ecoSaverToastVisibility)
        ) {
            handleShowEcoSaverCrouton();
        }
    }, [enabledCroutonId, handleShowEcoSaverCrouton, ecoSaverToastVisibility, shouldProceed, showExpressLottie]);

    return {
        ecoSaverLottieSuccess,
    };
};

export default useEcoSaverCrouton;
